import { useNavigate } from "react-router-dom";
import { useState, useMemo, useEffect } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import { VscEye } from "react-icons/vsc";
import moment from "moment";

import { CommonImages } from "../../../../reusableUI/images";
import Loader from "../../../../reusableUI/loader";
import ReusableTable from "../../../../reusableUI/table";
import Page from "../../../../reusableUI/page";

import { capitalizeFirstLetter } from "../../../../utils/Functions";
import {
  getUnassignedHCPNames,
  assignHCP,
  getBookingsDetails,
} from "../../../../Service/bookings";

const UnassignedBookings = ({ searchTerm }) => {
  const [loading, setLoading] = useState(true);
  const [paginationPage, setPaginationPage] = useState(0);
  const [unassignedList, setUnassignedList] = useState([]);
  const [hcpNames, setUnassignedHCPNames] = useState([]);
  const [selectedPageCount, setSelectedPageCount] = useState(() => {
    const saved = localStorage.getItem("pageCount");
    return saved ? parseInt(saved, 10) : 50;
  });
  const [sortBy, setSortBy] = useState(() => {
    const saved = localStorage.getItem("sortBy");
    return saved ? JSON.parse(saved) : [];
  });

  const navigate = useNavigate();

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      setLoading(true);
      const result = await getBookingsDetails();
      console.log("Unassigned Bookings fetched:", result);
      const bookingsData = Array.isArray(result?.data?.bookings)
        ? result.data.bookings
        : [];
      const unassignedBookings = bookingsData.filter(
        (booking) => booking?.assigned === false
      );
      setUnassignedList(unassignedBookings);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      setUnassignedList([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch HCP names
  useEffect(() => {
    const fetchHCPNames = async () => {
      try {
        const response = await getUnassignedHCPNames();
        if (response?.data && Array.isArray(response.data)) {
          setUnassignedHCPNames(response.data);
        } else if (Array.isArray(response)) {
          setUnassignedHCPNames(response);
        } else {
          setUnassignedHCPNames([]);
        }
      } catch (error) {
        console.error("Error fetching HCP names:", error);
        setUnassignedHCPNames([]);
      }
    };
    fetchHCPNames();
  }, []);

  // Fetch bookings on component mount

  const handleAssignChange = async (bookingId, hcpName) => {
    const selectedHCP = hcpNames.find((hcp) => hcp.name === hcpName);
    if (!selectedHCP) return;

    const payload = {
      visitID: bookingId,
      specialistInfo: {
        userID: selectedHCP.userID,
        name: selectedHCP.name,
        role: selectedHCP.role,
        subRole: selectedHCP.subRole,
        gender: selectedHCP.gender,
      },
    };

    console.log("Assigning HCP:", payload);

    try {
      setLoading(true);
      const response = await assignHCP(payload);
      console.log(response)
      if (response?.error === false) {
        fetchBookings();
        setUnassignedList((prev) =>
          prev.map((item) =>
            item._id === bookingId
              ? { ...item, assignedTo: hcpName, assigned: true }
              : item
          )
        );
      }
    } catch (error) {
      console.error("Failed to assign HCP:", error);
    } finally {
      setLoading(false);
    }
  };

  const navigateToProfile = (index, original) => {
    localStorage.setItem("profilePage", "user");
    navigate(`/bookingsProfile/${original._id}`, {
      state: { user: original, card: "2" },
    });
  };

  // Filtered data
  const filteredData = useMemo(() => {
    if (!searchTerm) return unassignedList;
    return unassignedList.filter((item) =>
      item?.patient?.name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [unassignedList, searchTerm]);

  // Table columns
  const columns = useMemo(() => {
    return [
      {
        Header: "S.No",
        id: "sno",
        Cell: ({ row }) => (
          <div className="text-center text-blue-600 text-sm">
            {paginationPage * selectedPageCount + row.index + 1}
          </div>
        ),
      },
      {
        Header: "Patient Name",
        accessor: "patient.name",
        Cell: ({ row: { original } }) => (
          <div className="flex items-center">
            <img
              src={CommonImages.defaultDp}
              alt="Profile"
              className="h-8 w-8 rounded-full"
              style={{ objectFit: "cover" }}
            />
            <span className="text-blue-600 ml-2 text-[13px] font-normal flex items-center">
              {capitalizeFirstLetter(original?.patient?.name || "N/A")}
            </span>
          </div>
        ),
      },
      {
        Header: "Gender",
        accessor: "patient.gender",
        Cell: ({ row: { original } }) => (
          <p className="ml-6">
            {capitalizeFirstLetter(original?.patient?.gender || "N/A")}
          </p>
        ),
      },
      {
        Header: "Location",
        accessor: "visitInfo.addressInfo.district",
        Cell: ({ row: { original } }) => (
          <div className="text-[12px] text-center max-w-[80px] truncate">
            {capitalizeFirstLetter(
              original?.visitInfo?.addressInfo?.district || "N/A"
            )}
          </div>
        ),
      },
      {
        Header: "Asked For",
        accessor: "specialistInfo.role",
        Cell: ({ row: { original } }) => (
          <div className="text-[12px] text-center max-w-[100px] truncate">
            {capitalizeFirstLetter(original?.specialistInfo?.role || "N/A")}
          </div>
        ),
      },
      {
        Header: "Duty Type",
        accessor: "dutyType",
        Cell: ({ row: { original } }) => (
          <div className="text-center text-[13px]">
            {capitalizeFirstLetter(original?.dutyType) || "-"}
          </div>
        ),
      },
      {
        Header: "Visit Dates",
        accessor: "visitInfo",
        Cell: ({ row: { original } }) => {
          const start = moment(original?.visitInfo?.startDate).format(
            "DD/MM/YYYY"
          );
          const end = moment(original?.visitInfo?.endDate).format("DD/MM/YYYY");
          return (
            <div className="text-center text-[13px] text-blue-600">
              {start} - {end}
            </div>
          );
        },
      },
      {
        Header: "Assign",
        accessor: "assignedTo",
        Cell: ({ row: { original } }) => {
          const askedRole = original?.specialistInfo?.role?.toLowerCase() || "";

          const filteredHcpList = useMemo(() => {
            if (!Array.isArray(hcpNames)) return [];
            return hcpNames.filter((hcp) => {
              const role = hcp.role?.toLowerCase();
              const subRole = hcp.subRole?.toLowerCase();

              if (askedRole === "nursing-assistant") {
                return role === "nurse" && subRole === "nursing-assistant";
              } else if (askedRole === "nurse") {
                return role === "nurse" && subRole !== "nursing-assistant";
              } else if (askedRole === "physiotherapist") {
                return role === "physiotherapist";
              } else {
                return false;
              }
            });
          }, [hcpNames, askedRole]);

          return (
            <div className="flex justify-center">
              <select
                value={original.assignedTo || ""}
                onChange={(e) =>
                  handleAssignChange(original._id, e.target.value)
                }
                className="border border-gray-300 rounded-md text-[13px] p-1 bg-white mr-2 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                <option value="" hidden>
                  Select HCP
                </option>
                {filteredHcpList.map((hcp, index) => (
                  <option key={index} value={hcp.name}>
                    {hcp.name}
                  </option>
                ))}
              </select>
            </div>
          );
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row: { original } }) => (
          <div className="text-center text-[13px] capitalize">
            {original?.status || "pending"}
          </div>
        ),
      },
      {
        Header: "View",
        accessor: "view",
        Cell: ({ row: { original } }) => (
          <div className="text-center">
            <button onClick={() => navigateToProfile(null, original)}>
              <VscEye size={18} color={"blue"} className="mr-4" />
            </button>
          </div>
        ),
      },
    ];
  }, [hcpNames, paginationPage, selectedPageCount]);

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page: tablePage,
    prepareRow,
    nextPage,
    previousPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    state: { sortBy: currentSortBy },
  } = useTable(
    {
      columns,
      data: filteredData,
      initialState: {
        pageSize: selectedPageCount,
        pageIndex: paginationPage,
        sortBy: sortBy.length > 0 ? sortBy : [],
      },
    },
    useSortBy,
    usePagination
  );

  useEffect(() => {
    localStorage.setItem("sortBy", JSON.stringify(currentSortBy));
  }, [currentSortBy]);

  return (
    <>
      {loading ? (
        <Loader loading={loading} />
      ) : (
        <div className="bg-[#F3F6F5]">
          <div className="overflow-x-auto bg-white rounded-2xl shadow-lg p-2 h-[87vh] m-2 flex flex-col gap-3">
            <div className="flex justify-between items-center">
              <p className="text-[14px] ml-2 font-bold pb-2">
                Unassigned Bookings ({filteredData.length})
              </p>
            </div>

            <div className="overflow-x-auto max-h-[75vh] mt-[-10px]">
              <div className="min-w-full w-[100%]">
                <ReusableTable
                  columns={columns}
                  getTableProps={getTableProps}
                  getTableBodyProps={getTableBodyProps}
                  headerGroups={headerGroups}
                  tablePage={tablePage}
                  prepareRow={prepareRow}
                />
                {/* {filteredData.length === 0 && (
                  <div className="text-center py-10 text-gray-500 text-sm">
                    No unassigned bookings found.
                  </div>
                )} */}
              </div>
            </div>

            <div className="mb-2">
              <Page
                paginationPage={paginationPage}
                nextPage={nextPage}
                previousPage={previousPage}
                canPreviousPage={canPreviousPage}
                canNextPage={canNextPage}
                pageOptions={pageOptions}
                screen="unassignedBookings"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default UnassignedBookings;
